import { css } from '@emotion/react';
import { Transition } from './mixins/_transition.mixin';
import { math } from 'polished';

import { BorderBottomRadius, BorderRadius, BorderTopRadius } from './mixins/_border-radius.mixin';
import { BoxShadow } from './mixins/_box-shadow.mixin';
import { MediaBreakpointUp } from './mixins/_breakpoints.mixin';
import { subtract } from './_functions';


export default ($) => css`
  .modal-open {
    overflow: hidden;

    .modal {
      overflow-x: hidden;
      overflow-y: auto;
    }
  }

  .modal {
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${$.zindexModal};
    display: none;
    width: 100%;
    height: 100%;
    overflow: hidden;
    outline: 0;
  }

  .modal-dialog {
    position: relative;
    width: auto;
    margin: ${$.modalDialogMargin};
    pointer-events: none;

    .modal.fade & {
      ${new Transition($.modalTransition).include($)};
      transform: ${$.modalFadeTransform};
    }

    .modal.show & {
      transform: ${$.modalShowTransform};
    }

    .modal.modal-static & {
      transform: ${$.modalScaleTransform};
    }
  }

  .modal-dialog-scrollable {
    display: flex;
    max-height: ${subtract("100%", math(`${$.modalDialogMargin} * 2`))};

    .modal-content {
      max-height: ${subtract("100vh", math(`${$.modalDialogMargin} * 2`))};
      overflow: hidden;
    }

    .modal-header,
    .modal-footer {
      flex-shrink: 0;
    }

    .modal-body {
      overflow-y: auto;
    }
  }

  .modal-dialog-centered {
    display: flex;
    align-items: center;
    min-height: ${subtract("100%", math(`${$.modalDialogMargin} * 2`))};

    &::before {
      display: block;
      height: ${subtract("100vh", math(`${$.modalDialogMargin} * 2`))};
      height: min-content;
      content: "";
    }

    &.modal-dialog-scrollable {
      flex-direction: column;
      justify-content: center;
      height: 100%;

      .modal-content {
        max-height: none;
      }

      &::before {
        content: none;
      }
    }
  }

  .modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    color: ${$.modalContentColor};
    pointer-events: auto;
    background-color: ${$.modalContentBg};
    background-clip: padding-box;
    border: ${$.modalContentBorderWidth} solid ${$.modalContentBorderColor};
    ${new BorderRadius($.modalContentBorderRadius).include($)};
    ${new BoxShadow($.modalContentBoxShadowXs).include($)};
    outline: 0;
  }

  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${$.zindexModalBackdrop};
    width: 100vw;
    height: 100vh;
    background-color: ${$.modalBackdropBg};

    &.fade { opacity: 0; }
    &.show { opacity: ${$.modalBackdropOpacity}; }
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: ${$.modalHeaderPadding};
    border-bottom: ${$.modalHeaderBorderWidth} solid ${$.modalHeaderBorderColor};
    ${new BorderTopRadius($.modalContentInnerBorderRadius).include($)};

    .close {
      padding: ${$.modalHeaderPadding};
      margin: ${math(`-${$.modalHeaderPaddingY}`)} ${math(`-${$.modalHeaderPaddingX}`)} ${math(`-${$.modalHeaderPaddingY}`)} auto;
    }
  }

  .modal-title {
    margin-bottom: 0;
    line-height: ${$.modalTitleLineHeight};
  }

  .modal-body {
    position: relative;
    flex: 1 1 auto;
    padding: ${$.modalInnerPadding};
  }

  .modal-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    padding: ${math(`${$.modalInnerPadding} - ${$.modalFooterMarginBetween} * 0.5`)};
    border-top: ${$.modalFooterBorderWidth} solid ${$.modalFooterBorderColor};
    ${new BorderBottomRadius($.modalContentInnerBorderRadius).include($)};

    > * {
      margin: ${math(`${$.modalFooterMarginBetween} * 0.5`)};
    }
  }

  .modal-scrollbar-measure {
    position: absolute;
    top: -9999px;
    width: 50px;
    height: 50px;
    overflow: scroll;
  }

  ${new MediaBreakpointUp("sm").include($, css`
    .modal-dialog {
      max-width: ${$.modalMd};
      margin: ${$.modalDialogMarginYSmUp} auto;
    }

    .modal-dialog-scrollable {
      max-height: ${subtract("100%", math(`${$.modalDialogMarginYSmUp} * 2`))};

      .modal-content {
        max-height: ${subtract("100vh", math(`${$.modalDialogMarginYSmUp} * 2`))};
      }
    }

    .modal-dialog-centered {
      min-height: ${subtract("100%", math(`${$.modalDialogMarginYSmUp} * 2`))};

      &::before {
        height: ${subtract("100vh", math(`${$.modalDialogMarginYSmUp} * 2`))};
        height: min-content;
      }
    }

    .modal-content {
      ${new BoxShadow($.modalContentBoxShadowSmUp).include($)};
    }

    .modal-sm { max-width: ${$.modalSm}; }
  `)}

  ${new MediaBreakpointUp("lg").include($, css`
    .modal-lg,
    .modal-xl {
      max-width: ${$.modalLg};
    }
  `)}

  ${new MediaBreakpointUp("xl").include($, css`
    .modal-xl { max-width: ${$.modalXl}; }
  `)}
`;
